import React from 'react';
import {
  BloodworkAnalysis,
  FurtherConsiderations,
  PanelMarker,
  Section,
  RootCauseCategory,
} from '../../models';
import { AnemiaStatus } from './bloodwork-anemia-status';
import { ReferenceRangeAnalysisContainer } from '../../containers/bloodwork-ref-range-analysis-container';
import { WBCEvaluation } from './bloodwork-wbc-evaluation';
import { GlucoseRegulationInsights } from './glucose-regulation-insights';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  title: string;
  section: Section;
  panelMarkers: PanelMarker[];
};

export class PDFSection extends React.Component<Props, {}> {
  furtherConsiderationsFor(
    rootCauseCategory: RootCauseCategory,
    section: string,
    tab: string,
  ): JSX.Element {
    const { furtherConsiderations } = this.props;
    return (
      <span key={rootCauseCategory}>
        {
          // tslint:disable-next-line:max-line-length
          furtherConsiderations
            .findConsiderations(rootCauseCategory, section, tab)
            .map((consideration, cindex) => {
              return (
                <span key={cindex}>
                  <span key={`title-${cindex}`} className="consideration-title">
                    {consideration.title}
                  </span>
                  <ul>
                    {consideration.items.map((item, itemIndex) => {
                      return (
                        <li key={`${rootCauseCategory}-${cindex}-${itemIndex}`}>
                          {item}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              );
            })
        }
      </span>
    );
  }

/*
  preparedFor(bloodworkAnalysis: BloodworkAnalysis) {
    return (
      <div>
        <h6 className="prepared-for-container">
          <strong>Prepared for: </strong>
          <span>
            {bloodworkAnalysis.bloodwork.client.firstName}{' '}
            {bloodworkAnalysis.bloodwork.client.lastName}
          </span>
          <strong style={{ marginLeft: 20 }}>Collection Date: </strong>
          <span>
            <Moment format="MM/DD/YYYY">
              {bloodworkAnalysis.bloodwork.collectionDate}
            </Moment>
          </span>
        </h6>
      </div>
    );
  }
*/

  evaluation(
    title: string,
    section: string,
    bloodworkAnalysis: BloodworkAnalysis,
  ) {
    // RBC, WBC, and GLUCOSE all render an upper "Insights" panel followed by
    // their bar graphs. We rely on the per-section JSX in
    // bloodwork-analysis-pdf.tsx to push these sections onto a fresh page
    // (via the surrounding `<div className="html2pdf__page-break" />`), so we
    // do NOT add another empty break div here — adding one when the prior
    // section already forced a break results in a blank page slipping in.
    if (section === Section.RBC) {
      return (
        <div>
          <h4 className="pdf-section-title"><strong>{title}</strong></h4>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <AnemiaStatus bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    } else if (section === Section.WBC) {
      return (
        <div>
          <h4 className="pdf-section-title"><strong>{title}</strong></h4>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <WBCEvaluation bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    } else if (section === Section.GLUCOSE) {
      return (
        <div>
          <h4 className="pdf-section-title"><strong>{title}</strong></h4>
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Evaluation',
          )}
          <div className="pdf-anemia-status">
            <GlucoseRegulationInsights bloodworkAnalysis={bloodworkAnalysis} />
          </div>
        </div>
      );
    }
    return null;
  }

  refRangeTitle(): string {
    const { section } = this.props;
    if (section === Section.WBC) {
      return 'Immune Activity Results';
    } else if (section === Section.PLATELETS) {
      return 'Blood Clotting Results';
    }
    else if (section === Section.PROTEINS) {
      return 'Protein Results';
    }
    else if (section === Section.NUTRIENTS) {
      return 'Nutrient Results';
    }
    else if (section === Section.SEX_HORMONE) {
      return 'Hormone Results';
    }
    else if (section === Section.INFLAMMATION_OXIDATIVE_STRESS) {
      return 'Inflammation & Oxidative Stress Results';
    }
    else if (section === Section.LIPIDS) {
      return 'Lipid Results';
    }
    else if (section === Section.LIVER_BILIARY) {
      return 'Liver Results';
    }
    else if (section === Section.ELECTROLYTES) {
      return 'Electrolyte Results';
    }
    else if (section === Section.GLUCOSE) {
      return 'Glucose Regulation & Metabolic Function Results';
    }
    return `${section} Results`;
  }

  render() {
    const { bloodworkAnalysis, panelMarkers, title, section } = this.props;
    if (panelMarkers.length === 0) {
      return null;
    }
    // Per-section notes are no longer rendered here — they are consolidated into
    // the Section Notes panel near the report bottom (SectionNotesSummaryPanel),
    // so a long note no longer competes with this section's bar graphs for space
    // on the page.

    const infoLink = `https://labsmarts.com/bibliography/#${section}`;
    // Sections that render their own panel + title in evaluation() already
    // have the section title at the top of the page. The bar-graphs wrapper
    // below uses page-break-inside: avoid (via .pdf-glucose-bar-graphs) so
    // when the bar-graphs don't fit on the Insights page they move whole
    // to the next page. In that case we want the title shown above them on
    // the new page so the reader knows what they are looking at; when they
    // DO fit on the Insights page, the title would be a duplicate and we
    // hide it.
    const hasEvaluationPanel =
      section === Section.RBC ||
      section === Section.WBC ||
      section === Section.GLUCOSE;
    // Layout estimates mirror the constants used by the PDF packer. We use
    // them to predict whether the bar-graphs wrapper will fit on the same
    // page as its Insights panel; if not, we keep the title.
    const PAGE_CAPACITY = 900;
    const BASE_HEIGHT = 180;
    const ROW_HEIGHT = 40;
    const SECTION_GAP = 24;
    const SAFETY_BUFFER = 60;
    // .pdf-glucose-bar-graphs adds padding-top to give the title a buffer
    // when the wrapper moves whole onto a new page; include it here so the
    // fit prediction matches the wrapper's actual rendered height.
    const KEEP_TOGETHER_PADDING_TOP = 16;
    // Estimates are tuned so that when the prediction says "fits", the bar
    // graphs really do render alongside the Insights panel on a single
    // page (and we hide the duplicate title), and when it says "doesn't
    // fit", html2pdf actually moves the bar-graphs wrapper to a new page
    // (and we keep the title at the top of that page).
    // The RBC Insights panel (AnemiaStatus) is not a fixed height — it grows
    // with each conditional finding it renders (nutrient-deficiency note, bone-
    // marrow overproduction, possible root-cause list, dehydration, poor
    // oxygenation, smoking). A single fixed estimate under-counts a panel full
    // of findings, so the predictor thinks the bar graphs fit beneath it and
    // hides the title — even though html2pdf actually pushes the Results onto a
    // fresh page, leaving them with no title. Estimate RBC from the same
    // signals AnemiaStatus uses so the prediction tracks the rendered height.
    const rbcEvaluationPanelHeight = () => {
      // Base = header + gauge + RBC size/color row (bare panel, no findings).
      // The fit test reduces to `evaluationHeight <= 620 - markers*40`, so at a
      // typical 8-marker RBC panel the budget is only ~300. The old base of 320
      // already blew that budget with ZERO findings, so 7-8 marker panels kept a
      // duplicate "Blood Oxygen & Red Blood Cell Function" title even though the
      // Results render on the same page as the Insights panel. 240 lets bare and
      // light/moderate-findings panels correctly predict "fits" (title hidden),
      // while findings-heavy panels (root-cause lists + multiple condition texts)
      // still exceed PAGE_CAPACITY via the additions below and keep the title on
      // their new page.
      let height = 240;
      if (bloodworkAnalysis.nutrientDeficiencyOrInflammationLikelyText) {
        height += 48;
      }
      if (bloodworkAnalysis.boneMarrowAnalysis?.boneMarrowOverproductionIsLikely) {
        height += 28;
      }
      const rootCauses = bloodworkAnalysis.possibleRootCauses?.() ?? [];
      if (rootCauses.length) {
        height += 32 + rootCauses.length * 24;
      }
      if (bloodworkAnalysis.isAnyDehydrationPossibleOrHigher?.()) {
        height += 48;
      }
      if (bloodworkAnalysis.isPoorOxygenationPossibleOrHigher?.()) {
        height += 48;
      }
      if (bloodworkAnalysis.isPoorOxygenationDueToSmoking?.()) {
        height += 48;
      }
      return height;
    };
    const EVALUATION_PANEL_HEIGHT: { [section: string]: number } = {
      [Section.RBC]: rbcEvaluationPanelHeight(),
      [Section.WBC]: 300,      // WBCEvaluation + considerations
      // GlucoseRegulationInsights grows with every add-on pattern box it
      // resolves, so it estimates its own height (see
      // GlucoseRegulationInsights.estimatedPdfHeight) rather than carrying a
      // fixed number here. A standard single-pattern panel measures 280 — the
      // value this predictor was previously tuned to, which keeps the common
      // case unchanged: the full 8-base-marker glucose panel (glucose, HbA1c,
      // insulin, C-peptide, TG/HDL, TG, HDL, amylase) still fits under the
      // gauge with margin (280+24+516+60 = 880 <= 900) and the duplicate title
      // stays hidden. The same estimate feeds the PDF section packer, so the
      // two views of this panel's height can't drift apart.
      [Section.GLUCOSE]: GlucoseRegulationInsights.estimatedPdfHeight(bloodworkAnalysis),
    };
    const barGraphsHeight =
      BASE_HEIGHT + panelMarkers.length * ROW_HEIGHT + KEEP_TOGETHER_PADDING_TOP;
    const evaluationHeight = EVALUATION_PANEL_HEIGHT[section] ?? 0;
    const barGraphsFitOnEvaluationPage =
      hasEvaluationPanel &&
      evaluationHeight + SECTION_GAP + barGraphsHeight + SAFETY_BUFFER <= PAGE_CAPACITY;
    const showBarGraphsTitle = !hasEvaluationPanel || !barGraphsFitOnEvaluationPage;
    // Apply page-break-inside: avoid to sections whose bar graphs would
    // otherwise be split mid-table by html2pdf. RBC/WBC/GLUCOSE need it
    // because their bar-graphs wrapper must stay together with (or move
    // whole below) their Insights panel above. INFLAMMATION can have up
    // to 13 markers and gets close to the page-capacity ceiling — without
    // keep-together html2pdf tends to leave the title at the bottom of
    // the prior page and overflow the bar graphs onto the next one.
    const needsKeepTogether =
      hasEvaluationPanel || section === Section.INFLAMMATION_OXIDATIVE_STRESS;
    const sectionWrapperClassName = needsKeepTogether ? 'pdf-glucose-bar-graphs' : '';
    return (
      <>
        {this.evaluation(title, section, bloodworkAnalysis)}
        <div className={sectionWrapperClassName}>
          {showBarGraphsTitle && <h4 className="pdf-section-title"><strong>{title}</strong></h4>}
          {this.furtherConsiderationsFor(
            RootCauseCategory.PDF,
            section,
            'Analysis',
          )}
          <div className="pdf-ref-range-analysis">
            <ReferenceRangeAnalysisContainer
              title={this.refRangeTitle()}
              section={section}
              bloodwork={bloodworkAnalysis.bloodwork}
              previousBloodwork={bloodworkAnalysis.previousBloodwork}
              infoLink={infoLink}
              panelMarkers={panelMarkers}
              forPdf
            />
          </div>
        </div>
        {/* <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Insights"
                    section={section}
                    tab="Interpretation"
                />                
                <PDFTab
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Additional Testing"
                    section={section}
                    tab="Testing"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Further Considerations"
                    section={section}
                    tab="Considerations"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Signs and Symptoms"
                    section={section}
                    tab="Symptoms"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Diet"
                    section={section}
                    tab="Diet"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Lifestyle"
                    section={section}
                    tab="Lifestyle"
                />
                <PDFTab 
                    bloodworkAnalysis={bloodworkAnalysis}
                    furtherConsiderations={furtherConsiderations}
                    title={title}
                    subtitle="Supplements"
                    section={section}
                    tab="Supplements"
                />
                */}
      </>
    );
  }
}
